import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { MoreHorizontal } from 'lucide-react'
import clsx from 'clsx'

/**
 * The one "⋯" menu the CRM uses — on a table row, a list chip and the profile
 * panel alike.
 *
 * Three surfaces needed the same thing and a hand-rolled dropdown in each
 * would have behaved three different ways to a keyboard. This one opens on
 * click, puts focus on its first item, walks items with ↑/↓, closes on Escape
 * or a click anywhere else, and hands focus back to the button that opened
 * it. It is portalled to <body> and positioned from the trigger's rectangle
 * because the table scrolls: a menu drawn inside an `overflow: auto` cell is
 * clipped by the cell the moment it opens near the bottom of the list.
 *
 * Escape is consumed in the capture phase and marked `defaultPrevented`, so
 * the page's own Escape handler — which closes the side panel — can tell a
 * key that closed a menu from one meant for the panel.
 */
/** Keys a table row acts on, which a menu or its trigger must keep to itself. */
const ROW_KEYS = new Set(['Enter', ' ', 'ArrowDown', 'ArrowUp'])

export function ActionMenu({
  label,
  children,
  trigger,
  align = 'end',
  className,
  menuClassName,
  size = 13,
}: {
  /** Accessible name of the trigger, e.g. "Actions for Ethan Thompson". */
  label: string
  /** What the open menu contains; `close` is what an item calls when done. */
  children: (close: () => void) => ReactNode
  /** A custom trigger; the default is a ⋯ glyph. */
  trigger?: ReactNode
  align?: 'start' | 'end'
  className?: string
  menuClassName?: string
  size?: number
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState<{ top: number; left?: number; right?: number }>({ top: 0 })

  const close = useCallback(() => {
    setOpen(false)
    // The trigger may have been unmounted by whatever the item did (a retired
    // row leaves the table); only a still-attached button takes focus back.
    const button = triggerRef.current
    if (button && document.contains(button)) button.focus()
  }, [])

  // Where to draw it: under the trigger, aligned to its edge, flipped above
  // when the viewport has no room below. Measured after the menu exists so
  // its real height decides the flip.
  useLayoutEffect(() => {
    if (!open) return
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const menuHeight = menuRef.current?.offsetHeight ?? 0
    const below = rect.bottom + 4
    const fits = typeof window === 'undefined' || below + menuHeight <= window.innerHeight || menuHeight === 0
    const top = fits ? below : Math.max(4, rect.top - menuHeight - 4)
    setPosition(
      align === 'end'
        ? { top, right: Math.max(4, (typeof window === 'undefined' ? 0 : window.innerWidth) - rect.right) }
        : { top, left: rect.left },
    )
  }, [open, align])

  useEffect(() => {
    if (!open) return
    const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"], input, button')
    first?.focus()

    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        close()
        return
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
      const items = Array.from(
        menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([disabled])') ?? [],
      )
      if (items.length === 0) return
      const index = items.indexOf(document.activeElement as HTMLElement)
      if (index === -1 && !menuRef.current?.contains(document.activeElement)) return
      event.preventDefault()
      const next = event.key === 'ArrowDown' ? (index + 1) % items.length : (index - 1 + items.length) % items.length
      items[next].focus()
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, close])

  const menu = open ? (
    <div
      ref={menuRef}
      role="menu"
      aria-label={label}
      style={{ position: 'fixed', zIndex: 60, ...position }}
      className={clsx(
        'card min-w-[176px] max-w-[320px] rounded-lg border border-amber-400/15 bg-[#0d0d0d] p-1 text-[11px] shadow-2xl',
        menuClassName,
      )}
      // The menu is portalled to <body>, but React events still bubble up
      // the component tree — through the table row that opened it. A click
      // on an item must not toggle the row, and Enter on an item must not
      // open the row's window.
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (ROW_KEYS.has(event.key)) event.stopPropagation()
      }}
    >
      {children(close)}
    </div>
  ) : null

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation()
          setOpen((current) => !current)
        }}
        onDoubleClick={(event) => event.stopPropagation()}
        // Enter and the arrows belong to the button, not to the table row
        // behind it. Escape is left to bubble: React stops a synthetic
        // event at its root container, and the page's document listener —
        // the one that closes the side panel — would never hear it.
        onKeyDown={(event) => {
          if (ROW_KEYS.has(event.key)) event.stopPropagation()
        }}
        className={clsx(
          'inline-flex items-center justify-center rounded text-stone-500 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
          open && 'text-amber-200',
          className,
        )}
      >
        {trigger ?? <MoreHorizontal size={size} />}
      </button>
      {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
    </>
  )
}

/** One choice on the menu. */
export function MenuItem({
  children,
  onSelect,
  danger,
  disabled,
  hint,
  icon,
}: {
  children: ReactNode
  onSelect: () => void
  danger?: boolean
  disabled?: boolean
  /** Why it is disabled, when it is. */
  hint?: string
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      title={disabled ? hint : undefined}
      onClick={onSelect}
      className={clsx(
        'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left transition-colors',
        danger ? 'text-red-300 hover:bg-red-400/10' : 'text-stone-300 hover:bg-amber-400/[0.08] hover:text-amber-100',
        'focus-visible:bg-amber-400/[0.08] focus-visible:outline-none',
        disabled && 'cursor-not-allowed text-stone-600 hover:bg-transparent hover:text-stone-600',
      )}
    >
      {icon ? <span className="text-stone-500">{icon}</span> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  )
}

/**
 * A yes/no step that takes the place of the menu items — "Retire Ethan
 * Thompson? Yes / No" — so a destructive action is one deliberate click past
 * the menu rather than a bare item somebody's thumb lands on.
 */
export function MenuConfirm({
  question,
  confirmLabel,
  onConfirm,
  onCancel,
  pending,
  error,
}: {
  question: string
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  pending?: boolean
  error?: string | null
}) {
  return (
    <div className="px-2 py-1.5" role="group" aria-label={question}>
      <div className="text-[11px] text-stone-200">{question}</div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <button
          type="button"
          role="menuitem"
          onClick={onConfirm}
          disabled={pending}
          className="rounded border border-red-400/30 bg-red-400/10 px-2 py-0.5 text-[10px] font-medium text-red-200 hover:bg-red-400/20 disabled:cursor-not-allowed disabled:text-stone-600"
        >
          {pending ? 'Working…' : confirmLabel}
        </button>
        <button
          type="button"
          role="menuitem"
          onClick={onCancel}
          className="rounded border border-amber-400/10 px-2 py-0.5 text-[10px] text-stone-400 hover:text-stone-200"
        >
          No
        </button>
      </div>
      {error ? <div className="mt-1 text-[10px] text-red-300">{error}</div> : null}
    </div>
  )
}
